import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertTriangle } from 'lucide-react'
import { useProductDialogs } from '@/components/features/checkout/useProductDialogs'
import { ProductDetailsDialog } from '@/components/features/checkout/ProductDetailsDialog'
import {
  AddOnAction,
  AddOnRow,
  AddOnStoreCard,
  PlanCardsLayout,
  PlanStoreCard,
  StoreExplainer,
  ProductStoreCard
} from '@/components/features/storefront/StorefrontSections'
import { KIND_ICON, planOptionFor, storeGridClass, type StoreCardLayout, type StoreItemKind } from '@/components/features/storefront/storefrontDisplay'
import { ViewSwitch } from '@/components/ui/view-switch'
import { AddAddOnDialog } from '@/components/features/billing/AddAddOnDialog'
import { useRoute } from '@/lib/RouteContext'
import { yearlyToggleSavings } from '@/lib/checkout/annual'
import { sdk } from '@/lib/sdk'
import { changePlanPath, pricingPath } from '@/lib/checkout/pricingPath'
import { openPlanCheckout, planCardOf, prefetchPlanCheckout } from '@/lib/checkout/planCheckoutNav'
import { useSubscription } from '@/lib/SubscriptionContext'
import { useMemberEntry } from '@/lib/hooks/useMemberEntry'
import { cn } from '@/lib/utils'
import { gaItem, trackSelectItem, trackViewItemList, type GaItem } from '@/lib/analytics/ecommerce'
import type { StorefrontAddOn, StorefrontPlan } from '@/types/storefront'
import type { PublicProduct, PublicSubscriptionGroup } from '@/types/checkout'

export type StoreView = 'all' | StoreItemKind

interface StoreOfferingsProps {
  plans: StorefrontPlan[]
  addOns: StorefrontAddOn[]
  products: PublicProduct[]
  /** The GA list these cards are reported in (`item_list_id`). */
  listId: string
  /** A product to open on arrival; `undefined` reads `?product=` from the address, `null` opens none. */
  preselect?: string | null
  /** The type pills, when the page keeps them in the address; local otherwise. */
  view?: StoreView
  onViewChange?: (view: StoreView) => void
  /** The billing interval of the plans, likewise. */
  billing?: string
  onBillingChange?: (billing: string) => void
  /**
   * The cards as equals (the padlocked space's offer): no featured item and no "Destaque". By count:
   * one product wide, two in two columns, three and more three per line.
   */
  plain?: boolean
  /** The "how buying works" strip above the cards. */
  explainer?: boolean
  /** Inside a block that already has the plans' band (the padlocked space's offer): no band of its own. */
  bare?: boolean
  /** The plan's page link; the padlocked space's offer adds where to come back once paid. */
  planLink?: (slug: string, optionKey?: string | null) => string
  /** After a product bought here is paid and closed. */
  onPurchased?: () => void
}

/**
 * The cards of the store and everything that buys from them (F-29 phase 3):
 * plans (to the plan's page), add-ons (added to the plan, a plan change, or
 * the plan that takes them) and one-off products (details and purchase
 * dialogs). Used by the storefront space and by the offer of a padlocked
 * space, so both sell the same way. What is listed, and each add-on's state
 * for this viewer, comes from the server.
 */
export function StoreOfferings({
  plans,
  addOns,
  products,
  listId,
  preselect,
  view: viewProp,
  onViewChange,
  billing: billingProp,
  onBillingChange,
  explainer = true,
  plain = false,
  bare = false,
  planLink = pricingPath,
  onPurchased
}: StoreOfferingsProps) {
  const { t } = useTranslation(['common', 'checkout'])
  const { navigate, currentPath } = useRoute()
  const [localView, setLocalView] = useState<StoreView>('all')
  const [localBilling, setLocalBilling] = useState('')
  const typeView = viewProp ?? localView
  const setTypeView = onViewChange ?? setLocalView
  const billing = billingProp ?? localBilling
  const setBilling = onBillingChange ?? setLocalBilling
  /* The add-on card being acted on. */
  const [addingAddOn, setAddingAddOn] = useState<StorefrontAddOn | null>(null)
  const [openedAddOn, setOpenedAddOn] = useState<StorefrontAddOn | null>(null)

  /* GA: a plan is reported by the option its "from" price is (the option ids are what checkout reports). */
  const planItem = (plan: StorefrontPlan, index?: number): GaItem => {
    const option = plan.options.find((o) => Number(o.price) === Number(plan.fromPrice)) || plan.options[0]
    return gaItem(
      { id: option?.id || plan.id, title: plan.name, type: option?.type, billingInterval: option?.billingInterval },
      { category: 'plan', price: plan.fromPrice, variant: option?.title, index }
    )
  }

  /* The whole listing as one GA list: products, plans and add-ons. */
  const listingKey = [...products, ...plans, ...addOns].map((item) => item.id).join(',')
  useEffect(() => {
    if (!listingKey) return
    trackViewItemList(listId, listId, [
      ...products.map((product) => gaItem(product)),
      ...plans.map((plan) => planItem(plan)),
      ...addOns.map((addOn) => gaItem(addOn, { category: 'add_on' }))
    ])
    // Keyed by the ids, not the arrays' identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listingKey, listId])

  /* Products bought on the spot: details, purchase and `?product=` links, as on the catalog. */
  const { openProduct, preselectMissing, dialogs } = useProductDialogs({
    products,
    ready: true,
    preselect,
    listId,
    trackListView: false,
    onPurchased
  })

  /* F-195: whoever already has a plan changes to another one on the plan change page, not the checkout;
     back here once it is done. */
  const { subscription } = useSubscription()
  const { suspended: salesClosed } = useMemberEntry()
  const back = currentPath
  const openPlan = (slug: string, optionKey?: string | null, addOnIds: string[] = []) => {
    const plan = plans.find((candidate) => candidate.slug === slug)
    if (plan) trackSelectItem(listId, listId, planItem(plan, plans.indexOf(plan)))
    if (subscription) navigate(changePlanPath(slug, optionKey, { addOnIds, back }))
    /* The plan's checkout opens whole on its first frame, the card's name and price flying to it. */
    else void openPlanCheckout({ navigate, path: planLink(slug, optionKey), source: planCardOf(document.activeElement) })
  }

  /* Billing toggle: only when the plans are billed at more than one interval. Monthly first. */
  const intervals = useMemo(() => {
    const found = new Set(plans.flatMap((plan) => plan.options.map((o) => o.billingInterval).filter((i): i is string => Boolean(i))))
    return ['MONTHLY', 'YEARLY'].filter((i) => found.has(i))
  }, [plans])
  /* Opens on the cheaper rhythm (David, 2026-10-30): yearly when it saves, else monthly. The viewer's pick wins. */
  const cheaper = yearlyToggleSavings(plans.map((plan) => plan.options)) ? 'YEARLY' : 'MONTHLY'
  const interval = intervals.length > 1
    ? (intervals.includes(billing) ? billing : intervals.includes(cheaper) ? cheaper : intervals[0])
    : null

  /* With "Anual", an add-on shows what the plans' yearly option charges for it: the public plan list has
     those amounts (`addOnAmounts`, the ones checkout adds). Loaded once, only when needed. */
  const [planGroups, setPlanGroups] = useState<PublicSubscriptionGroup[] | null>(null)
  const needsYearlyAddOns = interval === 'YEARLY' && addOns.length > 0
  useEffect(() => {
    if (!needsYearlyAddOns || planGroups) return
    let alive = true
    Promise.resolve(sdk.checkout.getSubscriptionGroups())
      .then((list) => { if (alive) setPlanGroups(Array.isArray(list) ? list : []) })
      .catch(() => { if (alive) setPlanGroups([]) })
    return () => { alive = false }
  }, [needsYearlyAddOns, planGroups])
  const yearlyAmountOf = (addOn: StorefrontAddOn): number | null | undefined => {
    if (interval !== 'YEARLY') return undefined
    if (!planGroups) return undefined
    for (const group of planGroups) {
      if (!addOn.plans.some((plan) => plan.id === group.id)) continue
      const amount = group.products
        .filter((option) => option.billingInterval === 'YEARLY')
        .flatMap((option) => option.addOnAmounts ?? [])
        .find((entry) => entry.productId === addOn.id)
      if (amount) return Number(amount.amount)
    }
    return null
  }

  /*
   * The layout follows how a buyer decides (David, 2026-10-30: "diferenciar o
   * que é um produto, uma assinatura e um adicional"): the featured item on
   * top, then Plans (the main decision), Add-ons (what extends a plan) and
   * Products (bought once). Each group has its own card anatomy: a pricing
   * table, compact rows, catalog cards. A group with one item shows it wide.
   */
  type Entry =
    | { kind: 'product'; id: string; item: PublicProduct }
    | { kind: 'plan'; id: string; item: StorefrontPlan }
    | { kind: 'addOn'; id: string; item: StorefrontAddOn }
  /* Add-ons the viewer's plan takes come first, then those a plan change unlocks; owned ones last. */
  const addOnOrder: Record<StorefrontAddOn['availability'], number> = { add: 0, upgrade: 1, subscribe: 2, owned: 3 }
  const entries: Entry[] = [
    ...plans.map((item): Entry => ({ kind: 'plan', id: item.id, item })),
    ...[...addOns]
      .sort((x, y) => addOnOrder[x.availability] - addOnOrder[y.availability])
      .map((item): Entry => ({ kind: 'addOn', id: item.id, item })),
    ...products.map((item): Entry => ({ kind: 'product', id: item.id, item }))
  ]
  const kindsWithItems = (['plan', 'addOn', 'product'] as const).filter((kind) => entries.some((e) => e.kind === kind))
  const many = entries.length > 6 && kindsWithItems.length > 1
  const view: StoreView = many && kindsWithItems.includes(typeView as StoreItemKind) ? typeView : 'all'
  const featured = !plain && view === 'all' && entries.length > 1
    ? entries.find((e) => e.kind === 'product' && e.item.isHighlighted && !e.item.soldOut)
      ?? entries.find((e) => e.kind === 'addOn' && e.item.isHighlighted)
      ?? null
    : null
  const rest = entries.filter((e) => e !== featured && (view === 'all' || e.kind === view))

  const subscribeToAddOn = (addOn: StorefrontAddOn) => {
    if (addOn.plans[0]?.slug) openPlan(addOn.plans[0].slug)
  }
  /* "Upgrade" on an add-on the current plan does not take: the plan change page, on a plan that takes it,
     with the add-on ticked. The option billed like today's plan (not the toggle's): that change is
     immediate, so the add-on is charged now; a change of rhythm waits for the renewal, and a plan renewed
     by PIX or boleto takes no add-on then. The page still checks with the quote and opens on one that works. */
  const upgradeForAddOn = (addOn: StorefrontAddOn) => {
    const target = addOn.plans.find((plan) => plan.slug && !plans.find((p) => p.id === plan.id)?.options.some((o) => o.id === subscription?.productId))
      ?? addOn.plans.find((plan) => plan.slug)
    if (!target?.slug) return
    const listed = plans.find((plan) => plan.id === target.id)
    const rhythm = subscription?.billingInterval || interval
    const option = listed && rhythm ? planOptionFor(listed, rhythm) : null
    navigate(changePlanPath(target.slug, option?.billingInterval === rhythm ? option?.optionKey : null, { addOnIds: [addOn.id], back }))
  }

  /* The whole add-on card: an add-on that needs another plan goes straight to the plan change page
     (David, 2026-10-30: no dialog flashing on the way); the others open their details. */
  const openAddOn = (addOn: StorefrontAddOn) => {
    if (addOn.availability === 'upgrade' && !salesClosed) upgradeForAddOn(addOn)
    else setOpenedAddOn(addOn)
  }

  const renderPlan = (plan: StorefrontPlan, layout: StoreCardLayout = 'grid') => (
    <PlanStoreCard
      key={plan.id}
      plan={plan}
      interval={interval}
      layout={layout}
      onSubscribe={(chosen, option) => chosen.slug && openPlan(chosen.slug, interval ? option?.optionKey : null)}
    />
  )
  const renderWide = (entry: Entry): ReactNode => {
    if (entry.kind === 'product') {
      return (
        <ProductStoreCard
          key={entry.id}
          product={entry.item}
          layout="wide"
          plain={plain}
          onOpen={() => openProduct(entry.item, 'details')}
          onBuy={() => openProduct(entry.item, 'purchase')}
          onRedeem={() => openProduct(entry.item, 'redeem')}
        />
      )
    }
    if (entry.kind === 'plan') return renderPlan(entry.item, 'wide')
    const addOn = entry.item
    return (
      <AddOnStoreCard
        key={entry.id}
        addOn={addOn}
        layout="wide"
        interval={interval}
        yearlyAmount={yearlyAmountOf(addOn)}
        onOpen={() => openAddOn(addOn)}
        onAdd={() => setAddingAddOn(addOn)}
        onUpgrade={() => upgradeForAddOn(addOn)}
        onSubscribe={() => subscribeToAddOn(addOn)}
      />
    )
  }

  /* Inside the offer's block, a single kind needs no heading of its own: the block's headline says it. */
  const headings = !bare || kindsWithItems.length > 1


  /* One anatomy per kind. */
  const groupBody = (kind: StoreItemKind, items: Entry[]): ReactNode => {
    if (kind === 'plan') {
      /* By count, the same layout as the plans page (PlanCardsLayout). */
      return (
        <PlanCardsLayout
          plans={items.map((e) => e.item as StorefrontPlan)}
          renderCard={(plan, { alone }) => alone ? (
            <PlanStoreCard
              plan={plan}
              interval={interval}
              alone
              onSubscribe={(chosen, option) => chosen.slug && openPlan(chosen.slug, interval ? option?.optionKey : null)}
            />
          ) : renderPlan(plan)}
        />
      )
    }
    if (kind === 'addOn') {
      return (
        /* Never wider than half the content: two per line from lg, a single one keeps its half. */
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {items.map((e) => {
            const addOn = e.item as StorefrontAddOn
            return (
              <AddOnRow
                key={addOn.id}
                addOn={addOn}
                interval={interval}
                yearlyAmount={yearlyAmountOf(addOn)}
                onOpen={() => openAddOn(addOn)}
                onAdd={() => setAddingAddOn(addOn)}
                onUpgrade={() => upgradeForAddOn(addOn)}
                onSubscribe={() => subscribeToAddOn(addOn)}
              />
            )
          })}
        </div>
      )
    }
    if (items.length === 1) return renderWide(items[0])
    return (
      <div className={plain ? cn('grid grid-cols-1 gap-6', items.length === 2 ? 'md:grid-cols-2' : 'md:grid-cols-2 lg:grid-cols-3') : storeGridClass(items.length)}>
        {items.map((e) => {
          const product = e.item as PublicProduct
          return (
            <ProductStoreCard
              key={product.id}
              product={product}
              coverSlot
              plain={plain}
              onOpen={() => openProduct(product, 'details')}
              onBuy={() => openProduct(product, 'purchase')}
              onRedeem={() => openProduct(product, 'redeem')}
            />
          )
        })}
      </div>
    )
  }

  const sectionMeta: Record<StoreItemKind, { label: string; hint: string }> = {
    plan: { label: t('common:storefront.sectionPlans'), hint: t('common:storefront.sectionPlansHint') },
    addOn: { label: t('common:storefront.sectionAddOns'), hint: t('common:storefront.sectionAddOnsHint') },
    product: { label: t('common:storefront.sectionProducts'), hint: t('common:storefront.sectionProductsHint') }
  }

  const typeSwitch = many ? (
    <ViewSwitch
      value={view}
      onChange={(next) => setTypeView(next as StoreView)}
      aria-label={t('common:storefront.viewLabel')}
      data-testid="storefront-type-view"
      options={[
        { value: 'all', label: t('common:storefront.viewAll'), count: entries.length },
        ...kindsWithItems.map((kind) => ({
          value: kind,
          label: sectionMeta[kind].label,
          count: entries.filter((e) => e.kind === kind).length
        }))
      ]}
    />
  ) : null
  /* The billing toggle, once, in the Plans heading: only when the plans bill at more than one interval. */
  const billingSwitch = interval ? (
    <div className="flex flex-shrink-0 items-center gap-2">
      <span className="text-xs text-gray-500">{t('common:storefront.billingLabel')}</span>
      <ViewSwitch
        value={interval}
        onChange={setBilling}
        aria-label={t('common:storefront.billingLabel')}
        data-testid="storefront-billing-toggle"
        className="w-auto flex-shrink-0"
        options={intervals.map((i) => (() => {
          const saving = i === 'YEARLY' ? yearlyToggleSavings(plans.map((plan) => plan.options)) : null
          return {
            value: i,
            label: t(i === 'YEARLY' ? 'checkout:yearly' : 'checkout:monthly'),
            badge: saving ? t(saving.upTo ? 'common:storefront.savingsUpTo' : 'common:storefront.savingsTag', { percent: saving.percent }) : null
          }
        })())}
      />
    </div>
  ) : null

  return (
    <>
      {preselectMissing && (
        <div role="status" className="mb-6 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>{t('checkout:purchase.catalog.linkUnavailable')}</span>
        </div>
      )}
      {/* The checkout's code and the plan list, fetched as the pointer reaches a plan card. */}
      <div className="space-y-10" onPointerOverCapture={(e) => { if (planCardOf(e.target as Element)) prefetchPlanCheckout() }}>
        {explainer && <StoreExplainer kinds={kindsWithItems} />}
        {typeSwitch}
        {featured && <div data-testid="storefront-featured">{renderWide(featured)}</div>}
        {kindsWithItems.map((kind) => {
          const items = rest.filter((e) => e.kind === kind)
          if (items.length === 0) return null
          const Icon = KIND_ICON[kind]
          return (
            <section
              key={kind}
              data-testid={`storefront-section-${kind}`}
              aria-labelledby={headings ? `storefront-section-${kind}-title` : undefined}
              className={cn(
                /* Phones: no band chrome, so the plan cards span the content width like every other card. */
                kind === 'plan' && !bare && 'relative isolate sm:overflow-hidden sm:rounded-2xl sm:border sm:border-primary-100 sm:bg-gradient-to-br sm:from-primary-50 sm:via-white sm:to-primary-50 sm:px-8 sm:py-8'
              )}
            >
              {/* The Plans band (David, 2026-10-30): soft blurred tints of the community's color behind the
                  pricing cards, so they float on something instead of an empty page. Static, decorative only. */}
              {kind === 'plan' && !bare && (
                <>
                  <span aria-hidden="true" className="pointer-events-none absolute hidden sm:block -right-24 -top-28 -z-10 h-80 w-80 rounded-full bg-primary-200/40 blur-3xl" />
                  <span aria-hidden="true" className="pointer-events-none absolute hidden sm:block -bottom-32 -left-20 -z-10 h-72 w-72 rounded-full bg-primary-100/70 blur-3xl" />
                </>
              )}
              {headings ? (
                <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                  <div className="flex items-center gap-3">
                    <span className={cn('flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg text-primary-700', kind === 'plan' || bare ? 'bg-white shadow-sm' : 'bg-primary-50')}>
                      <Icon className="h-[18px] w-[18px]" aria-hidden="true" />
                    </span>
                    <div>
                      <h2 id={`storefront-section-${kind}-title`} className="font-semibold text-gray-900">{sectionMeta[kind].label}</h2>
                      <p className={cn('text-sm', kind === 'plan' || bare ? 'text-gray-600' : 'text-gray-500')}>{sectionMeta[kind].hint}</p>
                    </div>
                  </div>
                  {kind === 'plan' && billingSwitch}
                </div>
              ) : (
                kind === 'plan' && billingSwitch && <div className="mb-6 flex justify-center">{billingSwitch}</div>
              )}
              {groupBody(kind, items)}
            </section>
          )
        })}
      </div>

      {dialogs}
      <ProductDetailsDialog
        product={openedAddOn}
        open={Boolean(openedAddOn)}
        onOpenChange={(open) => !open && setOpenedAddOn(null)}
        action={openedAddOn && (
          <div className="space-y-3" data-testid="storefront-addon-details-action">
            {openedAddOn.plans.length > 0 && (openedAddOn.availability === 'owned' || openedAddOn.availability === 'add') && (
              <p className="text-xs text-gray-500">
                {t('common:storefront.addOnOfPlans', { plans: openedAddOn.plans.map((plan) => plan.name).join(', '), count: openedAddOn.plans.length })}
              </p>
            )}
            <AddOnAction
              addOn={openedAddOn}
              wide
              onAdd={() => { const a = openedAddOn; setOpenedAddOn(null); setAddingAddOn(a) }}
              onUpgrade={() => { const a = openedAddOn; setOpenedAddOn(null); upgradeForAddOn(a) }}
              onSubscribe={() => { const a = openedAddOn; setOpenedAddOn(null); subscribeToAddOn(a) }}
            />
          </div>
        )}
      />
      <AddAddOnDialog
        open={Boolean(addingAddOn)}
        onOpenChange={(open) => !open && setAddingAddOn(null)}
        preselectProductId={addingAddOn?.id ?? null}
      />
    </>
  )
}
